import { createFileRoute } from "@tanstack/react-router";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — Code Cubs Academy" },
      { name: "description", content: "Get in touch or book a free trial session." },
    ],
  }),
  component: ContactPage,
});

const schema = z.object({
  name: z.string().trim().min(2).max(80),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(5).max(30),
  childAge: z.string().trim().min(1).max(20),
  message: z.string().trim().min(5).max(1000),
});

function ContactPage() {
  const { t, language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["site-settings", language],
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("*");
      if (error) throw error;
      return data.reduce(
        (acc, row) => {
          acc[row.key] = language === "ar" ? row.value_ar || row.value_en : row.value_en;
          return acc;
        },
        {} as Record<string, string | null>,
      );
    },
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const form = new FormData(formEl);
    const data = {
      name: String(form.get("name") ?? ""),
      email: String(form.get("email") ?? ""),
      phone: String(form.get("phone") ?? ""),
      childAge: String(form.get("childAge") ?? ""),
      message: String(form.get("message") ?? ""),
    };
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("contact_submissions").insert({
      name: data.name,
      email: data.email,
      phone: data.phone,
      subject: `Child age ${data.childAge}`,
      message: data.message,
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(t.contact.success);
    formEl.reset();
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl lg:text-6xl font-extrabold">{t.contact.title}</h1>
        <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">{t.contact.desc}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        <div className="space-y-4 lg:col-span-1">
          {[
            {
              Icon: Mail,
              label: t.contact.emailLabel,
              v: settings?.contact_email || "hello@codecubs.academy",
            },
            {
              Icon: Phone,
              label: t.contact.phoneLabel,
              v: settings?.contact_phone || "+20 100 000 0000",
            },
            {
              Icon: MapPin,
              label: t.contact.addressLabel,
              v: settings?.contact_address || t.contact.addressValue,
            },
          ].map((it, i) => (
            <Card key={i} className="rounded-3xl border-0 shadow-soft">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-gradient-primary flex items-center justify-center text-primary-foreground">
                  <it.Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">{it.label}</div>
                  <div className="font-semibold">{it.v}</div>
                </div>
              </CardContent>
            </Card>
          ))}

          {settings?.contact_whatsapp && (
            <a
              href={`https://wa.me/${settings.contact_whatsapp.replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="rounded-3xl border-0 shadow-soft bg-green-50/50 hover:bg-green-50 transition-colors">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-[#25D366] flex items-center justify-center text-white">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">WhatsApp</div>
                    <div className="font-semibold">{settings.contact_whatsapp}</div>
                  </div>
                </CardContent>
              </Card>
            </a>
          )}
        </div>

        <Card className="rounded-3xl border-0 shadow-soft lg:col-span-2">
          <CardContent className="p-8">
            <h2 className="font-bold text-2xl mb-6">{t.contact.bookSession}</h2>
            <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t.contact.name}</Label>
                <Input id="name" name="name" required maxLength={80} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t.contact.email}</Label>
                <Input id="email" name="email" type="email" required maxLength={200} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t.contact.phone}</Label>
                <Input id="phone" name="phone" required maxLength={30} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="childAge">{t.contact.childAge}</Label>
                <Input id="childAge" name="childAge" type="number" min={4} max={18} required />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="message">{t.contact.message}</Label>
                <Textarea id="message" name="message" rows={4} required maxLength={1000} />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="sm:col-span-2 rounded-full bg-gradient-primary text-primary-foreground border-0 h-12"
              >
                {submitting ? t.contact.sending : t.contact.send}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
